import { useState } from "react"
import { useStripe, useElements } from "@stripe/react-stripe-js"
import AldooStoreAPI from "aldoo-ra/Store/api"

const usePaymentSubmission = (store) => {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [ready, setReady] = useState(false)
  const { onPaymentSuccess, paymentInfo, onPaymentError } = store

  const completeSubscriptionAfterSetup = async (setupIntentId) => {
    try {
      const response = await AldooStoreAPI.completeSubscriptionAfterSetup({
        pid: paymentInfo.pid,
        setupIntentId,
        promo_code: paymentInfo.promoCode,
      })
      if (response.error) throw new Error(response.error)
      return response // Return response with paymentIntentId and subscriptionId
    } catch (error) {
      throw error
    }
  }

  const handleSubmitWithSavedMethod = async () => {
    if (!stripe) return
    setLoading(true)
    try {
      // First determine if we're dealing with a SetupIntent or PaymentIntent
      const isSetupIntent = paymentInfo.clientSecret.startsWith("seti_")
      const isPaymentIntent = paymentInfo.clientSecret.startsWith("pi_")
      let result

      const paymentMethodOptions = {
        payment_method: paymentInfo.savedPaymentMethod.id,
      }

      let subscriptionResponse = null

      if (isSetupIntent) {
        // confirmCardSetup removed in 2026-03-25.dahlia — use confirmSetup
        result = await stripe.confirmSetup({
          clientSecret: paymentInfo.clientSecret,
          confirmParams: {
            ...paymentMethodOptions,
            return_url: `${window.location.origin}/payment-result`,
          },
          redirect: 'if_required',
        })
        if (!result.error && result.setupIntent?.id && paymentInfo.pid) {
          subscriptionResponse = await completeSubscriptionAfterSetup(result.setupIntent.id)
        }
      } else if (isPaymentIntent) {
        result = await stripe.confirmCardPayment(
          paymentInfo.clientSecret,
          paymentMethodOptions
        )
      } else if (paymentInfo.type === "free_subscription") {
        // Free subscriptions don't need confirmation
        result = { error: null }
      } else {
        throw new Error("Unknown intent type")
      }

      if (result.error) {
        throw result.error
      }
      onPaymentSuccess({
        paymentIntentId: subscriptionResponse?.paymentIntentId,
        subscriptionId: subscriptionResponse?.subscriptionId || subscriptionResponse?.subscription
      })
    } catch (error) {
      onPaymentError(error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    setLoading(true)
    if (!stripe) return
    if (!elements) return
    const return_url = `${window.location.origin}/payment-result`

    try {
      let subscriptionResponse = null

      // Regular payment flow with elements
      if (paymentInfo.type === "trial_setup") {
        // Handle trial setup - confirm once
        const { setupIntent, error: setupError } = await stripe.confirmSetup({
          elements,
          redirect: "if_required",
          confirmParams: {
            return_url,
          },
        })

        // Already succeeded on a previous attempt — proceed with the existing setup intent
        if (setupError?.code === "setup_intent_unexpected_state" && setupError?.setup_intent?.status === "succeeded") {
          subscriptionResponse = await completeSubscriptionAfterSetup(setupError.setup_intent.id)
        } else {
          if (setupError) return onPaymentError(setupError)
          // Setup successful, now complete the subscription
          subscriptionResponse = await completeSubscriptionAfterSetup(setupIntent.id)
        }
      } else {
        // Handle regular payment or setup intent (check client secret prefix)
        const isSetupIntent = paymentInfo.clientSecret?.startsWith("seti_")
        let result

        if (isSetupIntent) {
          const { setupIntent, error } = await stripe.confirmSetup({
            elements,
            redirect: "if_required",
            confirmParams: { return_url },
          })
          // If the setup intent already succeeded (e.g. retry after a server-side failure),
          // skip confirmation and proceed with the existing setup intent ID.
          if (error?.code === "setup_intent_unexpected_state" && error?.setup_intent?.status === "succeeded") {
            subscriptionResponse = await completeSubscriptionAfterSetup(error.setup_intent.id)
            result = { error: null }
          } else {
            result = { error }
            if (!error) {
              subscriptionResponse = await completeSubscriptionAfterSetup(setupIntent.id)
            }
          }
        } else {
          result = await stripe.confirmPayment({
            elements,
            redirect: "if_required",
            confirmParams: { return_url },
          })
        }

        if (result.error) return onPaymentError(result.error)
      }

      // Payment is successful - pass paymentIntentId for polling
      onPaymentSuccess({
        paymentIntentId: subscriptionResponse?.paymentIntentId,
        subscriptionId: subscriptionResponse?.subscriptionId || subscriptionResponse?.subscription
      })
    } catch (err) {
      onPaymentError(err)
    } finally {
      setLoading(false)
    }
  }

  return {
    handleSubmit,
    handleSubmitWithSavedMethod,
    loading,
    setLoading,
    ready,
    setReady,
    stripe,
    elements,
    isReady: stripe && ready && !loading,
  }
}

export default usePaymentSubmission
